import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HubEntitiesSortType, HubEntityType } from '@core/interfaces/hub-entity-type';
import { HubHttpPaginationResponse } from '@core/interfaces/HubResponse';
import {
  BrowseSourcesResponse,
  BulkFetchSourceContentItem,
  BulkFetchSourceContentResponse,
  CreateAssetData,
  FetchSourceContentResponse,
  HubCreateModuleBody,
  HubCreateModuleResponse,
  HubModule,
  ModuleAssets,
  OracleSchemaData,
  RegisterEmulationResponse,
  RunEmulationResponse,
  SapFunction,
  TestConnectionResult,
} from '@core/interfaces/module';
import { CreateBySolutionResponse } from '@core/interfaces/solution';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { environment } from '@env';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Pagination } from '@core/interfaces/pagination';
import { BulkIdsResponse } from '@core/interfaces/common';
import { BulkNamesResponse } from '@core/interfaces/common';
import { ConnectorsOptionsWrapper } from '@core/interfaces/connection-profile';
import { DeleteBulkResponse } from '@core/interfaces/bulk-operations';
import { splitLabelFilter } from '@core/interfaces/label-filter';

@Injectable({
  providedIn: 'root',
})
export class ModulesService {
  private http = inject(HttpClient);
  private readonly permissionsService = inject(PermissionsService);

  private readonly entryPoint = `${environment.BACKEND_DOMAIN}/api/v1/modules`;

  all(query?: {
    projectId?: string;
    sort?: string;
    excludeProjects?: string;
    pagination?: Pagination;
    projectVersion?: number;
    excludeModuleTypes?: string;
    includeModuleTypes?: string;
    search?: string;
    labels?: string[];
  }): Observable<HubHttpPaginationResponse<HubModule>> {
    let queryParams: HttpParams;
    queryParams = new HttpParams({
      fromObject: {
        sort: `${query?.sort || HubEntitiesSortType.MODIFIED_DESC}`,
      },
    });
    if (query?.labels && query.labels.length > 0) {
      const { labels, noneLabels } = splitLabelFilter(query.labels);
      if (labels.length > 0) {
        queryParams = queryParams.set('labels', labels.join(','));
      }
      queryParams = queryParams.set('noneLabels', noneLabels.toString());
    }
    if (query?.projectId) {
      queryParams = queryParams.set('projectId', query.projectId);
    }

    if (query?.excludeProjects) {
      queryParams = queryParams.set('exclude_project', query.excludeProjects);
    }

    if (query?.includeModuleTypes) {
      queryParams = queryParams.set('module_type', query.includeModuleTypes);
    }

    if (query?.excludeModuleTypes) {
      queryParams = queryParams.set('exclude_module_type', query.excludeModuleTypes);
    }

    if (query?.pagination?.limit !== undefined && query?.pagination?.limit !== null) {
      queryParams = queryParams.set('limit', query.pagination.limit.toString());
    }

    if (query?.pagination?.offset !== undefined && query?.pagination?.offset !== null) {
      queryParams = queryParams.set('offset', query.pagination.offset.toString());
    }

    if (query?.projectVersion) {
      queryParams = queryParams.set('projectVersion', `${query.projectVersion}`);
    }

    if (query?.search) {
      queryParams = queryParams.set('search', query.search);
    }

    return this.http
      .get<HubHttpPaginationResponse<HubModule>>(this.entryPoint, {
        params: queryParams,
      })
      .pipe(
        // TODO: TEMPORAL MAP, REMOVE AFTER BACKEND REFACTOR
        // IMPORTANT: DONT FORGET TO REMOVE IN TESTS
        map((res) =>
          Array.isArray(res.elements)
            ? res
            : {
                elements: [],
                total: 0,
              },
        ),
      );
  }

  /** Get all module names for validation (e.g. duplicate check before create). */
  names(): Observable<BulkNamesResponse> {
    return this.http.get<BulkNamesResponse>(`${this.entryPoint}/names`);
  }

  // this endpoint should be removed after backend refactor.
  additionalInfo(
    id: string,
    hubEntity?: HubEntityType.Asset | HubEntityType.Project,
  ): Observable<Array<{ id: string; name: string; versionCount?: number }>> {
    const typeFlyout = hubEntity === HubEntityType.Project ? 'projects' : 'assets';

    return this.http
      .get<{ elements?: Array<{ id: string; name: string; versionCount?: number }> }>(
        `${this.entryPoint}/${id}/flyout`,
        {
          params: {
            type: typeFlyout,
          },
        },
      )
      .pipe(
        // TODO: TEMPORAL MAP, REMOVE AFTER BACKEND REFACTOR
        // IMPORTANT: DONT FORGET TO REMOVE IN TESTS
        map((res) => (res.elements ? res.elements : [])),
      );
  }

  show(id: string): Observable<HubModule> {
    const url = `${this.entryPoint}/${id}`;
    return (
      this.http
        .get<HubModule>(url)
        // TODO: TEMPORAL MAP, REMOVE AFTER BACKEND REFACTOR
        .pipe(
          map((res) => {
            const fullRes = {
              ...{
                name: '',
                additionalInfo: {
                  type: '',
                  numOfAssets: 0,
                  numOfProjects: 0,
                },
                config: {
                  connectorName: '',
                  connectorVersion: '',
                  moduleName: '',
                },
                description: '',
                modifiedAt: new Date().toString(),
                modifiedBy: '',
              },
              ...res,
            };
            return fullRes;
          }),
        )
    );
  }

  // remove this endpoint after refactor. useless. same as additionalInfo
  projectsByModuleId(id: string): Observable<{ id: string; name: string }[]> {
    return this.http
      .get<{ elements: { id: string; name: string }[] }>(`${this.entryPoint}/${id}/flyout`, {
        params: {
          type: 'projects',
        },
      })
      .pipe(map((response) => response?.elements ?? []));
  }

  update(id: string, data: { name: string; description: string }): Observable<void> {
    const url = `${this.entryPoint}/${id}`;
    return this.http.put<void>(url, data);
  }

  duplicate(id: string, name: string): Observable<HubModule> {
    return this.http.post<HubModule>(`${this.entryPoint}/${id}/duplicate`, { name });
  }

  delete(id: string): Observable<void> {
    const url = `${this.entryPoint}/${id}`;
    return this.http.delete<void>(url);
  }

  destroyBulk(ids: string[]): Observable<DeleteBulkResponse> {
    return this.http.delete<DeleteBulkResponse>(`${this.entryPoint}/bulk/delete`, {
      body: { ids },
    });
  }

  createBySolution(
    solutionName: string,
    moduleName?: string,
  ): Observable<CreateBySolutionResponse> {
    return this.http.post<CreateBySolutionResponse>(`${this.entryPoint}/create-by-solution`, {
      solutionName,
      moduleName,
    });
  }

  createAssets(moduleId: string, fetchRequest: CreateAssetData): Observable<BulkIdsResponse> {
    return this.http.post<BulkIdsResponse>(`${this.entryPoint}/${moduleId}/assets`, fetchRequest);
  }

  create(body: HubCreateModuleBody): Observable<HubCreateModuleResponse> {
    return this.http
      .post<HubCreateModuleResponse>(this.entryPoint, body)
      .pipe(tap(() => this.permissionsService.invalidateCurrentUserResourceRoles()));
  }

  runEmulation(moduleId: string): Observable<RunEmulationResponse> {
    return this.http.post<RunEmulationResponse>(`${this.entryPoint}/${moduleId}/emulation`, {});
  }

  registerEmulationSession(sessionId: string): Observable<RegisterEmulationResponse> {
    return this.http.post<RegisterEmulationResponse>(
      `${this.entryPoint}/${sessionId}/emulation/session`,
      {},
    );
  }

  testDBConnection(
    moduleId: string,
    connectionProperties: {
      type: string;
      url: string;
      username: string;
      password: string;
    },
  ): Observable<TestConnectionResult> {
    return this.http.post<TestConnectionResult>(
      `${this.entryPoint}/${moduleId}/test-db-connection`,
      { connectionProperties },
    );
  }

  dbOptions(
    moduleId: string,
    params?: {
      schemaName?: string;
      packageName?: string;
      profile?: string;
      useAllTable?: boolean;
      sapFunctionSearchText?: string;
      timeout?: number;
      includeStoredProcedures?: boolean;
      as400library?: string;
    },
  ): Observable<{ data: OracleSchemaData }> {
    const queryParams = new HttpParams({
      fromObject: params || {},
    });
    return this.http.get<{ data: OracleSchemaData }>(
      `${this.entryPoint}/${moduleId}/fetch-options`,
      { params: queryParams },
    );
  }

  dbOptionsSap(
    moduleId: string,
    params?: {
      profile?: string;
      sapFunctionSearchText?: string;
      timeout?: number;
    },
  ): Observable<SapFunction[]> {
    const queryParams = new HttpParams({
      fromObject: params || {},
    });
    return this.http
      .get<{ data: { functions: SapFunction[] } }>(`${this.entryPoint}/${moduleId}/fetch-options`, {
        params: queryParams,
      })
      .pipe(map((value) => value.data.functions));
  }

  changeConnection(moduleId: string, newConnectionId: string): Observable<void> {
    return this.http.put<void>(`${this.entryPoint}/${moduleId}/connection/${newConnectionId}`, {});
  }

  getAssetsForModules(modulesIds: string[]): Observable<ModuleAssets> {
    return this.http.post<ModuleAssets>(`${this.entryPoint}/assets/read-bulk`, { ids: modulesIds });
  }

  optionsByBulk(connectorNames: string[]): Observable<ConnectorsOptionsWrapper> {
    return this.http.post<ConnectorsOptionsWrapper>(
      `${this.entryPoint}/connectors/bulk/read-options`,
      { names: connectorNames },
    );
  }

  /**
   * Browse source files (COBOL, CPY, etc.) using the connector behind the scenes.
   * This enables browsing source physical files and their members.
   *
   * @param moduleId The module ID whose connection profile will be used
   * @param params Query parameters for browsing
   */
  browseSources(
    moduleId: string,
    params?: {
      profile?: string;
      library?: string;
      sourceFile?: string;
      deep?: boolean;
      objectType?: string;
    },
  ): Observable<BrowseSourcesResponse> {
    let queryParams = new HttpParams();
    if (params?.profile) {
      queryParams = queryParams.set('profile', params.profile);
    }
    if (params?.library) {
      queryParams = queryParams.set('library', params.library);
    }
    if (params?.sourceFile) {
      queryParams = queryParams.set('sourceFile', params.sourceFile);
    }
    if (params?.deep !== undefined) {
      queryParams = queryParams.set('deep', params.deep.toString());
    }
    if (params?.objectType) {
      queryParams = queryParams.set('objectType', params.objectType);
    }
    return this.http.get<BrowseSourcesResponse>(`${this.entryPoint}/${moduleId}/browse-sources`, {
      params: queryParams,
    });
  }

  fetchSourceContent(
    moduleId: string,
    params: {
      library: string;
      profile?: string;
      sourceFile?: string;
      memberName?: string;
      pattern?: string;
    },
  ): Observable<FetchSourceContentResponse> {
    let queryParams = new HttpParams().set('library', params.library);
    if (params?.profile) {
      queryParams = queryParams.set('profile', params.profile);
    }
    if (params?.sourceFile) {
      queryParams = queryParams.set('sourceFile', params.sourceFile);
    }
    if (params?.memberName) {
      queryParams = queryParams.set('memberName', params.memberName);
    }
    if (params?.pattern) {
      queryParams = queryParams.set('pattern', params.pattern);
    }
    return this.http.get<FetchSourceContentResponse>(
      `${this.entryPoint}/${moduleId}/fetch-source-content`,
      { params: queryParams },
    );
  }

  bulkFetchSourceContent(
    moduleId: string,
    params: {
      profile?: string;
      items: BulkFetchSourceContentItem[];
      requestTimeout?: number;
    },
  ): Observable<BulkFetchSourceContentResponse> {
    return this.http.post<BulkFetchSourceContentResponse>(
      `${this.entryPoint}/${moduleId}/fetch-source-content/bulk`,
      {
        profile: params.profile ?? 'default',
        items: params.items,
        requestTimeout: params.requestTimeout,
      },
    );
  }
}
